import { Component, computed, inject, input, signal } from '@angular/core';
import {
	MDHeaderComponent,
	MDTagComponent,
	MDContentBoxComponent,
	MDDropDownComponent,
	MD_DIALOG_REF,
	IMDDropDownOption,
	MDDialogComponent
} from '@melodic-ui/components';
import { GradeTagComponent } from '../../grade-tag/grade-tag.component';
import { LoaderComponent } from '../../loader/loader.component';
import { CommonModule } from '@angular/common';
import { EnrollmentDetailService } from '@lb/api/mylb/services/enrollment-detail.service';
import { ISemesterGradeSummary } from '@lb/api/mylb/interfaces/lesson/isemester-grade-summary.interface';
import { IFinalGradeSaveRequest } from '@lb/api/mylb/interfaces/enrollment/ifinal-grade-save-request.interface';
import { IEnrollmentDetailItem } from '@lb/api/mylb/interfaces/enrollment/ienrollment-detail.interface';
import { catchError, forkJoin, of } from 'rxjs';
import { ICoreGrade } from '@lb/api/core/interfaces';
import { IndicatorType } from '@melodic-ui/types';
import { GradeStateService } from '@lb/state/grade-state.service';
import { resolveNearestGrade } from '@lb/functions/nearest-grade.function';

export interface IReviewItem {
	categoryID: number;
	categoryName: string;
	weightPercent: number;
	gradeName: string;
	gradeColor: IndicatorType;
	calculatedGradeID: number | null;
	effectiveGradeID: number | null;
	overrideGradeID: number | null;
	isOverridden: boolean;
	isProjectGrade: boolean;
}

@Component({
	selector: 'lb-semester-grade-review',
	templateUrl: './semester-grade-review.component.html',
	styleUrl: './semester-grade-review.component.scss',
	standalone: true,
	imports: [
		CommonModule,
		MDHeaderComponent,
		MDTagComponent,
		MDContentBoxComponent,
		MDDropDownComponent,
		GradeTagComponent,
		LoaderComponent,
		MDDialogComponent
	]
})
export class SemesterGradeReviewComponent {
	private readonly dialogRef = inject(MD_DIALOG_REF);
	private readonly enrollmentDetailService = inject(EnrollmentDetailService);
	private readonly gradeState = inject(GradeStateService);

	// ── Dialog Inputs ──
	enrollmentID = input.required<number>();
	semesterID = input.required<number>();

	// ── State ──
	loading = signal(true);
	error = signal<string | null>(null);
	saving = signal(false);

	gradeSummary = signal<ISemesterGradeSummary | null>(null);
	// The dialog has no scale of its own to key off, so it grades against the default scale.
	grades = computed<ICoreGrade[]>(() => this.gradeState.defaultGrades() ?? []);

	// Per-category override state: categoryID → overrideGradeID
	categoryOverrides = signal<Map<number, number | null>>(new Map());
	semesterOverrideGradeID = signal<number | null>(null);

	// ── Derived ──
	reviewItems = computed<IReviewItem[]>(() => {
		const summary = this.gradeSummary();
		if (!summary) return [];
		const overrides = this.categoryOverrides();
		return summary.categories.map((cat) => ({
			effectiveGradeID: overrides.get(cat.gradingTemplateCategoryID) ?? cat.calculatedGradeID ?? null,
			categoryID: cat.gradingTemplateCategoryID,
			categoryName: cat.categoryName,
			weightPercent: cat.weightPercent,
			gradeName: cat.calculatedGradeName ?? '—',
			gradeColor: (cat.calculatedGradeColor as IndicatorType) ?? 'default',
			calculatedGradeID: cat.calculatedGradeID,
			overrideGradeID: overrides.get(cat.gradingTemplateCategoryID) ?? null,
			isOverridden: overrides.has(cat.gradingTemplateCategoryID) && overrides.get(cat.gradingTemplateCategoryID) != null,
			isProjectGrade: cat.isProjectGrade
		}));
	});

	semesterFinalGrade = computed(() => {
		const summary = this.gradeSummary();
		if (!summary) return null;

		const computedGrade = this.getWeightedSemesterGrade();
		if (computedGrade) {
			return {
				gradeID: computedGrade.gradeID,
				gradeName: computedGrade.gradeName,
				color: this.getGradeColorByID(computedGrade.gradeID)
			};
		}

		return {
			gradeID: summary.overallCalculatedGradeID,
			gradeName: summary.overallCalculatedGradeName ?? '—',
			color: (summary.overallCalculatedGradeColor as IndicatorType) ?? 'default'
		};
	});

	overrideGradeOptions = computed<IMDDropDownOption[]>(() => {
		return [{ value: null, label: 'No Override' }, ...this.grades().map((g) => ({ value: g.gradeID, label: g.gradeName }))];
	});

	constructor() {
		// Kick off data load after inputs are set (Angular resolves required inputs before constructor for dialogs)
		queueMicrotask(() => {
			this.gradeState.loadGradeScales();
			this.loadData();
		});
	}

	// ── Data Loading ──
	private loadData(): void {
		this.loading.set(true);
		this.error.set(null);

		// The detail read exists only to prefill overrides already recorded for this semester — an
		// entitled admin re-opening a submitted semester from the Enrollment Summary. It is optional: if
		// it fails, the dialog still opens on the calculated grades with no overrides, exactly as a
		// first submission does.
		forkJoin({
			summary: this.enrollmentDetailService.getCalculatedSemesterGrades(this.enrollmentID(), this.semesterID()),
			detail: this.enrollmentDetailService.getEnrollmentDetail(this.enrollmentID()).pipe(catchError(() => of(null)))
		}).subscribe({
			next: ({ summary, detail }) => {
				this.gradeSummary.set(summary);
				this.prefillRecordedOverrides(detail);
				this.loading.set(false);
			},
			error: () => {
				this.error.set('Failed to load grade data. Please try again.');
				this.loading.set(false);
			}
		});
	}

	// A submitted semester carries its overrides on the enrollment detail. Starting from them means a
	// re-submission that touches nothing re-sends the recorded state instead of silently clearing every
	// override — and the API then writes no audit row, because nothing changed.
	private prefillRecordedOverrides(detail: IEnrollmentDetailItem | null): void {
		const semester = detail?.semesters?.find((s) => s.semesterID === this.semesterID());
		if (!semester) return;

		const overrides = new Map<number, number | null>();
		for (const semesterGrade of semester.semesterGrades ?? []) {
			if (semesterGrade.isOverriden && semesterGrade.overridenGradeID != null) {
				overrides.set(semesterGrade.gradingTemplateCategoryID, semesterGrade.overridenGradeID);
			}
		}
		this.categoryOverrides.set(overrides);

		const final = semester.semesterFinalGrade;
		this.semesterOverrideGradeID.set(final?.isOverriden && final.overridenGradeID != null ? final.overridenGradeID : null);
	}

	// ── Actions ──
	onCancel(): void {
		this.dialogRef.close(false);
	}

	onSubmit(): void {
		const summary = this.gradeSummary();
		if (!summary) return;

		this.saving.set(true);
		const overrides = this.categoryOverrides();
		const semesterOverride = this.semesterOverrideGradeID();

		const computedSemesterGradeID = this.getWeightedSemesterGrade()?.gradeID ?? summary.overallCalculatedGradeID ?? 0;

		const request: IFinalGradeSaveRequest = {
			enrollmentID: this.enrollmentID(),
			semesterID: this.semesterID(),
			overallCalculatedGradeID: computedSemesterGradeID,
			overallOverrideGradeID: semesterOverride,
			isOverriden: semesterOverride != null,
			categoryGrades: summary.categories.map((cat) => ({
				enrollmentID: this.enrollmentID(),
				semesterID: this.semesterID(),
				gradingTemplateCategoryID: cat.gradingTemplateCategoryID,
				overallCalculatedGradeID: cat.calculatedGradeID ?? 0,
				overallOverrideGradeID: overrides.get(cat.gradingTemplateCategoryID) ?? null,
				isOverriden: overrides.has(cat.gradingTemplateCategoryID) && overrides.get(cat.gradingTemplateCategoryID) != null
			}))
		};

		this.enrollmentDetailService.saveFinalGrades(request).subscribe({
			next: () => {
				this.saving.set(false);
				this.dialogRef.close(true);
			},
			error: () => {
				this.saving.set(false);
				this.error.set('Failed to save grades. Please try again.');
			}
		});
	}

	onProjectOverrideChanged(event: unknown, categoryID: number): void {
		const gradeID = event as number | null;
		const overrides = new Map(this.categoryOverrides());
		if (categoryID != null) {
			overrides.set(categoryID, gradeID);
			this.categoryOverrides.set(overrides);
		}
	}

	onFinalGradeOverrideChanged(event: unknown): void {
		const gradeID = event as number | null;
		this.semesterOverrideGradeID.set(gradeID);
	}

	private getWeightedSemesterGrade(): ICoreGrade | null {
		// Weighted average of the categories' grade values — the same Core.Grades.Value ordinal the
		// per-category lesson averages are computed on — resolved with the shared nearest-grade rule
		// (midpoint to the higher grade), so all three grade calculations agree.
		const grades = this.grades();
		if (!grades.length) return null;

		const gradeValueByID = new Map<number, number>(grades.map((g) => [g.gradeID, g.value]));
		let weightedSum = 0;
		let totalWeight = 0;

		for (const item of this.reviewItems()) {
			const effectiveGradeID = item.effectiveGradeID;
			if (effectiveGradeID == null || item.weightPercent <= 0) continue;

			const value = gradeValueByID.get(effectiveGradeID);
			if (value == null) continue;

			weightedSum += value * item.weightPercent;
			totalWeight += item.weightPercent;
		}

		if (totalWeight <= 0) return null;

		return resolveNearestGrade(weightedSum / totalWeight, grades);
	}

	private getGradeColorByID(gradeID: number | null): IndicatorType {
		if (gradeID == null) return 'default';

		const categoryMatch = this.gradeSummary()?.categories.find((c) => c.calculatedGradeID === gradeID);
		if (categoryMatch?.calculatedGradeColor) {
			return categoryMatch.calculatedGradeColor as IndicatorType;
		}

		const summary = this.gradeSummary();
		if (summary?.overallCalculatedGradeID === gradeID && summary.overallCalculatedGradeColor) {
			return summary.overallCalculatedGradeColor as IndicatorType;
		}

		return 'default';
	}
}
